import { graphql, useStaticQuery } from 'gatsby';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import React, { useState } from 'react';
import Slider from 'react-slick';
import Image from 'src/elements/image/image';
import { LeftArrow, RightArrow } from 'src/elements/slider/slider.arrows.styles';
import Heading from 'src/elements/typography/heading';
import formatParagraph from 'src/functions/formatParagraph';
import { DotItem, DotsContainer } from 'src/molecules/creators/creators.images.styles';
import CreatorsConfetti from '../creators/creators.confetti';
import {
  DescriptionP,
  ImageContainers,
  MightyNewsCompanies,
  MightyNewsContainer,
  MightyNewsWrapper,
  PressImage,
  PressSectionCarouselItem,
  SliderContainer,
  PressImageContainer
} from './mightyNetworksNews.styles';

const MightyNetworksNews = (): JSX.Element => {
  const [ activeSlider, setActiveSlider ] = useState(0);
  const [ isSwiping, setIsSwiping ] = useState(false);
  const [ isDragging, setIsDragging ] = useState(false);

  const sliderSettings = {
    infinite: true,
    draggable: true,
    slidesToShow: 3,
    slidesToScroll: 1,
    nextArrow: <RightArrow isHidden={false} />,
    prevArrow: <LeftArrow isHidden={false} />,
    responsive: [
      {
        breakpoint: 950, // layout.subLg
        settings: {
          slidesToShow: 2
        }
      },
      {
        breakpoint: 700, // layout.sm
        settings: {
          slidesToShow: 2,
          dots: true,
          arrows: false,
        }
      },
      {
        breakpoint: 500, // layout.xs
        settings: {
          slidesToShow: 1,
          dots: true,
          arrows: false,
        }
      }
    ],
    appendDots: (dots: any) => (
      <DotsContainer>
        {dots}
      </DotsContainer>
    ),
    swipeEvent: () => {
      setIsSwiping(true)

      setTimeout(() => setIsSwiping(false), 1000) // If the user starts to drag but stays in the same slide
    },
    onSwipe: () => setIsSwiping(false),
    beforeChange: (_oldIndex: number, newIndex: number) => {
      setIsDragging(true);
      setActiveSlider(newIndex);
    },
    afterChange: () => {
      setIsDragging(false);
    },
    customPaging: (index: number) => (
      <DotItem active={activeSlider === index}/>
    ),
  };

  const {
    fastCompany,
    wallStreetJournal,
    bloomberg,
    investLike,

    // slider images
    press1,
    press2,
    press3,
    press4,
    press5,
    press6,
    press7,
    press8,
    press9,
    press10,
    press11,
    press12,
    press13,
    press14,
    press15
  }: {
    fastCompany: { gatsbyImageData: IGatsbyImageData },
    wallStreetJournal: { gatsbyImageData: IGatsbyImageData },
    bloomberg: { gatsbyImageData: IGatsbyImageData },
    investLike: { gatsbyImageData: IGatsbyImageData },
    press1: { gatsbyImageData: IGatsbyImageData },
    press2: { gatsbyImageData: IGatsbyImageData },
    press3: { gatsbyImageData: IGatsbyImageData },
    press4: { gatsbyImageData: IGatsbyImageData },
    press5: { gatsbyImageData: IGatsbyImageData },
    press6: { gatsbyImageData: IGatsbyImageData },
    press7: { gatsbyImageData: IGatsbyImageData },
    press8: { gatsbyImageData: IGatsbyImageData },
    press9: { gatsbyImageData: IGatsbyImageData },
    press10: { gatsbyImageData: IGatsbyImageData },
    press11: { gatsbyImageData: IGatsbyImageData },
    press12: { gatsbyImageData: IGatsbyImageData },
    press13: { gatsbyImageData: IGatsbyImageData },
    press14: { gatsbyImageData: IGatsbyImageData },
    press15: { gatsbyImageData: IGatsbyImageData },
  } = useStaticQuery(graphql`{
    fastCompany: contentfulAsset(contentful_id: {eq: "3oD4IhTQmXbHtH5YrduNMl"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    wallStreetJournal: contentfulAsset(contentful_id: {eq: "4z49qxoFlDSfbeuN3tOibp"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    bloomberg: contentfulAsset(contentful_id: {eq: "48N36YD1KMRUIwa3JAtlJ9"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    investLike: contentfulAsset(contentful_id: {eq: "4UPTFReTXPhmXnq6LPyPRj"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    press1: contentfulAsset(contentful_id: {eq: "5pFWKe0G1dx0IhSWbYJbjm"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    press2: contentfulAsset(contentful_id: {eq: "1bNFGokBpJ4WHe5Bd9Yfdn"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    press3: contentfulAsset(contentful_id: {eq: "6h2ALZnLG1ECCbWGhW7Obb"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    press4: contentfulAsset(contentful_id: {eq: "28kkgWbIi0iHlISlvHnDSW"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    press5: contentfulAsset(contentful_id: {eq: "3Yr5rjRmtjl1Bze9Nrn1Sw"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    press6: contentfulAsset(contentful_id: {eq: "1m1GES4W0BEHWG3Ev2mNdx"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    press7: contentfulAsset(contentful_id: {eq: "4oCKM5mqnLDabYhyVZO5dy"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    press8: contentfulAsset(contentful_id: {eq: "1i7qiNzFn1auUI8LhyOiX3"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    press9: contentfulAsset(contentful_id: {eq: "6jXgUu037UF2vdCESVq84k"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    press10: contentfulAsset(contentful_id: {eq: "1u0FUwuE4GwjA40Dwt4WcX"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    press11: contentfulAsset(contentful_id: {eq: "1Ikffvrvhmsxmwps9lFWWf"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    press12: contentfulAsset(contentful_id: {eq: "ZAHekf7jJ1ytpNBjuMhl7"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    press13: contentfulAsset(contentful_id: {eq: "7qkSRAxdc8jA7dzU04AArT"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    press14: contentfulAsset(contentful_id: {eq: "2z60DqaQUfsJuoeOdoPDz9"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    press15: contentfulAsset(contentful_id: {eq: "3V8CGq0QFeotjoVP4ZmACC"}) {
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
  }
  `)

  const pressImages: ({ gatsbyImageData: IGatsbyImageData } & { link: string, description: string })[] = [
    {
      ...press1,
      link: 'https://www.fastcompany.com/90690440/what-is-an-independent-creator-vs-instagram-influencer',
      description: 'Fast Company'
    },
    {
      ...press2,
      link: 'https://open.spotify.com/episode/0qOjy03MUKJ3qflFa6JTkB',
      description: 'Invest Like the Best with Patrick O’Shaughnessy'
    },
    {
      ...press3,
      link: 'https://soundcloud.com/acremades/ep-388-gina-bianchini-on-selling-her-business-for-150-million-and-raising-50-million',
      description: 'Gina Bianchini On Selling Her Last Business For $150 Million And Now Raising $50 Million To Bring Communities Together, Deal Makers with Alejandro Cremades.'
    },
    {
      ...press4,
      link: 'https://www.theinformation.com/articles/content-alone-will-kill-the-creator-economy',
      description: 'The Information'
    },
    {
      ...press5,
      link: 'https://www.youtube.com/watch?v=Gx54ZJwzQ6E',
      description: 'The Culture Lab with Aga Bajers'
    },
    {
      ...press6,
      link: 'https://www.youtube.com/watch?v=5aRaBkQrxcM',
      description: 'The CREATOR MIDDLE CLASS and the Passion Economy ft. Gina Bianchini of Mighty Networks \\n \\n From 1 to 100'
    },
    {
      ...press7,
      link: 'https://open.spotify.com/episode/0gJ698IzMQ5L7IhWahBLIJ?si=QtKLYX06RCOVxqd5IOfGsg',
      description: 'Creative Elements'
    },
    {
      ...press8,
      link: 'https://www.youtube.com/watch?v=PQI6y_KKWDI',
      description: 'Why these companies are betting that online education is here to stay. \\n \\n CNBC'
    },
    {
      ...press9,
      link: 'https://techcrunch.com/2021/04/26/mighty-networks-raises-50m-to-build-a-creator-economy-for-the-masses/',
      description: 'TechCrunch'
    },
    {
      ...press10,
      link: 'https://www.youtube.com/watch?v=I1ciKXPvMtk',
      description: 'Building a System to Support the Growing "Content Economy" \\n \\n Bloomberg Technology'
    },
    {
      ...press11,
      link: 'https://listen.casted.us/public/5/Casting-a-Vision-beea9f47/2f9f0771',
      description: 'Casting a Vision'
    },
    {
      ...press12,
      link: 'https://nypost.com/2021/04/27/facebook-alternative-mighty-networks-closes-50-million-in-funding/',
      description: 'Facebook alternative Mighty Networks closes $50 million in funding \\n \\n New York Post'
    },
    {
      ...press13,
      link: 'https://podcasts.apple.com/gb/podcast/ep-36-the-passion-economy-building-business-through/id1526182731?i=1000515351055',
      description: 'The Mom Feed'
    },
    {
      ...press14,
      link: 'https://www.fastcompany.com/90628915/gina-bianchinis-mighty-networks-wants-to-help-middle-class-creators-take-control-of-their-destinies',
      description: 'Gina Bianchini’s Mighty Networks wants to help ‘middle-class creators’ take control of their destinies \\n \\n Fast Company'
    },
    {
      ...press15,
      link: 'https://www.youtube.com/watch?v=MZkSQlWAeGs',
      description: 'Lead The Future Podcast'
    }
  ];

  const jsxResult: JSX.Element[] = [];

  for (const press of pressImages) {
    jsxResult.push((
      <PressSectionCarouselItem
        onDragStart={(event) => {
          event.stopPropagation()
          event.preventDefault()
        }}
        onDrag={(event) => {
          event.stopPropagation()
          event.preventDefault()
        }}
        onClick={(event) => (isDragging || isSwiping) && event.preventDefault()}
        href={`${press.link}`}
        target="_blank"
        rel="noreferrer"
      >
        <PressImageContainer>
          <PressImage image={press.gatsbyImageData} alt="Press" />
        </PressImageContainer>
        <DescriptionP>{formatParagraph(press.description)}</DescriptionP>
      </PressSectionCarouselItem>
    ))
  }

  return (
    <MightyNewsWrapper>
      <CreatorsConfetti />
      <MightyNewsContainer>
        <Heading
          text="Mighty Networks in the News"
          size="3rem"
          align="center"
        />
        <MightyNewsCompanies>
          <ImageContainers>
            <Image childImageSharp={fastCompany.gatsbyImageData} alt="fastCompany" />
          </ImageContainers>
          <ImageContainers className="wallstreet-journal">
            <Image childImageSharp={wallStreetJournal.gatsbyImageData} alt="wallStreetJournal" />
          </ImageContainers>
          <ImageContainers>
            <Image childImageSharp={bloomberg.gatsbyImageData} alt="bloomberg" />
          </ImageContainers>
          <ImageContainers className="invest-like">
            <Image childImageSharp={investLike.gatsbyImageData} alt="investLike" />
          </ImageContainers>
        </MightyNewsCompanies>
        <SliderContainer>
          <Slider {...sliderSettings}>
            { jsxResult }
          </Slider>
        </SliderContainer>
      </MightyNewsContainer>
    </MightyNewsWrapper>
  );
};

export default MightyNetworksNews;
